/**
 * All-ponds grid: ponds (rows) × slots (cols). Each cell is a completion chip +
 * DO/slot summary; clicking drills into single-pond on the same date.
 */
import { useWaterSlotRules } from '@/hooks/useWaterSlotRules';
import { cn } from '@/lib/cn';
import {
  ALL_SLOTS,
  GRID_PARAMS,
  SLOT_META,
  dbColumnFor,
  isApplicable,
  tierFor,
} from '@/lib/waterModel';
import { useFarmThresholds } from '@/hooks/useFarmThresholds';
import type { ThresholdTable } from '@/lib/thresholds';
import type { ReadingsBySlot, SlotFieldsOff } from '@/lib/waterModel';
import type { ChecklistPond, ReadingSlot } from '@/types/entry';

interface Props {
  ponds: ChecklistPond[];
  readingsByCycle: Map<string, ReadingsBySlot>;
  onPick: (pondId: string) => void;
}

type ChipState = 'done' | 'partial' | 'empty' | 'critical';

// The second alkalinity number, calcium and magnesium are optional extras: a slot that was DONE
// before the number pairs stays DONE, and a value in one of them never turns a chip to PART.
const OPTIONAL_PAIR_KEYS = new Set(['alkalinity2', 'calcium', 'magnesium']);

function slotCell(table: ThresholdTable | undefined, values: ReadingsBySlot[ReadingSlot] | undefined, slot: ReadingSlot, off?: SlotFieldsOff) {
  const params = GRID_PARAMS.filter((p) => isApplicable(p.key, slot, off));
  let filled = 0;
  let counted = 0;
  let optionalFilled = false;
  let critical = false;
  let doValue: number | null = null;
  for (const p of params) {
    const raw = values?.[dbColumnFor(p.key)];
    const v = raw === null || raw === undefined || raw === '' ? null : Number(raw);
    if (p.key === 'dissolvedOxygen') doValue = typeof v === 'number' && !Number.isNaN(v) ? v : null;
    if (!OPTIONAL_PAIR_KEYS.has(p.key)) counted++;
    if (raw !== null && raw !== undefined && raw !== '') {
      if (OPTIONAL_PAIR_KEYS.has(p.key)) optionalFilled = true;
      else filled++;
      if (p.type === 'number' && v !== null && !Number.isNaN(v) && tierFor(table, p.key, v, values) === 'critical') {
        critical = true;
      }
    }
  }
  const total = counted;
  let state: ChipState = 'empty';
  if (critical) state = 'critical';
  else if (total > 0 && filled === total) state = 'done';
  else if (filled > 0 || optionalFilled) state = 'partial';
  const detail = SLOT_META[slot].type === 'DO'
    ? doValue !== null ? `DO ${doValue}` : '—'
    : `${filled}/${total}`;
  return { state, detail };
}

const chipClass: Record<ChipState, string> = {
  done: 'bg-emerald-100 text-emerald-700',
  partial: 'bg-amber-100 text-amber-700',
  empty: 'border border-dashed border-gray-300 text-gray-400',
  critical: 'bg-red-100 text-red-700',
};
const chipLabel: Record<ChipState, string> = { done: 'DONE', partial: 'PART', empty: '—', critical: 'CRIT' };

export function AllPondsGrid({ ponds, readingsByCycle, onPick }: Props) {
  const { table } = useFarmThresholds();
  const slotOff = useWaterSlotRules();
  const active = ponds.filter((p) => p.activeCycle);
  if (active.length === 0) {
    return <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-400">No active cycles on this date.</div>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-gray-50">
            <th className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-left font-medium text-gray-500 min-w-[140px]">Pond</th>
            {ALL_SLOTS.map((slot) => (
              <th key={slot} className="px-2 py-2 text-center font-medium text-gray-700 min-w-[92px]">
                {SLOT_META[slot].label}
                <div className="text-[10px] font-normal text-gray-400">
                  {SLOT_META[slot].type === 'FULL' ? 'Full test' : 'DO only'}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {active.map((pond) => {
            const byCycle = readingsByCycle.get(pond.activeCycle!.id) ?? {};
            const doneSlots = ALL_SLOTS.filter((s) => slotCell(table, byCycle[s], s, slotOff).state === 'done').length;
            // The checklist lists a crop on dates before its stocking (DOC <= 0); nothing is due then.
            const notStocked = pond.activeCycle!.doc <= 0;
            return (
              <tr key={pond.id} className="border-t border-gray-100">
                <th className="sticky left-0 z-10 bg-white px-3 py-2 text-left font-normal">
                  <div className="font-medium text-gray-800">{pond.name}</div>
                  <div className="text-[11px] text-gray-400">
                    {notStocked ? 'Not stocked yet' : `${doneSlots}/${ALL_SLOTS.length} slots · DOC ${pond.activeCycle!.doc}`}
                  </div>
                </th>
                {notStocked ? (
                  <td colSpan={ALL_SLOTS.length} aria-disabled className="border border-gray-100 bg-gray-50 p-1 text-center text-xs text-gray-400">
                    Not stocked yet on this date
                  </td>
                ) : (
                  ALL_SLOTS.map((slot) => {
                    const { state, detail } = slotCell(table, byCycle[slot], slot, slotOff);
                    return (
                      <td key={slot} className="border border-gray-100 p-1 text-center">
                        <button
                          onClick={() => onPick(pond.id)}
                          className="flex w-full flex-col items-center gap-1 rounded px-1 py-1 hover:bg-gray-50"
                          title={`Open ${pond.name} · ${slot}`}
                        >
                          <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-semibold', chipClass[state])}>
                            {chipLabel[state]}
                          </span>
                          <span className="text-[11px] tabular-nums text-gray-500">{detail}</span>
                        </button>
                      </td>
                    );
                  })
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
